import type {
  Facility,
  SearchParams,
  SearchResponse,
  Availability,
  AvailabilityRequest,
  Notification,
  OriginRecord,
  PlaceRecord,
  PlaceSummary,
  PlaceDetails,
  FacilityRecord,
  SiteRecord,
  AvailabilityRecord,
  NotificationChannelRecord,
  TelegramLinkTokenResponse,
  UserPlan,
  FacilityAvailabilityResponse,
  FacilityRefreshResponse,
  DirectoryPark,
  FeaturedPark,
} from '../types';

const API_BASE = import.meta.env.PROD && import.meta.env.VITE_API_PROXY_TARGET
  ? `${import.meta.env.VITE_API_PROXY_TARGET}/api`
  : '/api';

async function fetchApi<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: `HTTP error! status: ${response.status}` }));
    const message =
      (typeof error?.message === 'string' && error.message) ||
      (typeof error?.error === 'string' && error.error) ||
      (typeof error?.description === 'string' && error.description) ||
      `HTTP error! status: ${response.status}`;
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  facilities: {
    search: (params: SearchParams) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      return fetchApi<SearchResponse>(`/facilities?${queryParams}`);
    },

    getById: (id: string) => 
      fetchApi<Facility>(`/facilities/${id}`),
  },

  availability: {
    getForCampsite: (campsiteId: string, startDate: string, endDate: string) => {
      const params = new URLSearchParams({ startDate, endDate });
      return fetchApi<Availability[]>(`/availability/${campsiteId}?${params}`);
    },
    getForFacility: (facilityDbId: string, startDate: string, endDate: string) => {
      const params = new URLSearchParams({ startDate, endDate });
      return fetchApi<FacilityAvailabilityResponse>(`/facilities/${facilityDbId}/availability?${params}`);
    },
    refreshFacility: (facilityDbId: string) =>
      fetchApi<FacilityRefreshResponse>(`/facilities/${facilityDbId}/refresh`, {
        method: 'POST',
      }),
  },

  requests: {
    list: () => 
      fetchApi<AvailabilityRequest[]>('/availability-requests'),
    
    create: (data: {
      facilityId: string;
      startDate: string;
      endDate: string;
      stayLength: number;
      arrivalDays: Array<number | string>;
      siteType?: string;
      electric?: string;
      water?: boolean;
      sewer?: boolean;
      includeAdaSites?: boolean;
      isActive?: boolean;
    }) =>
      fetchApi<AvailabilityRequest>('/availability-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    
    createBatch: (data: {
      facilityIds: string[];
      startDate: string;
      endDate: string;
      stayLength: number;
      arrivalDays: Array<number | string>;
      siteType?: string;
      electric?: string;
      water?: boolean;
      sewer?: boolean;
      includeAdaSites?: boolean;
      isActive?: boolean;
    }) =>
      fetchApi<AvailabilityRequest[]>('/availability-requests/batch', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: {
      startDate?: string;
      endDate?: string;
      stayLength?: number;
      arrivalDays?: Array<number | string>;
      isActive?: boolean;
      siteType?: string;
      electric?: string;
      water?: boolean;
      sewer?: boolean;
      includeAdaSites?: boolean;
    }) =>
      fetchApi<AvailabilityRequest>(`/availability-requests/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    cancel: (id: number) =>
      fetchApi<void>(`/availability-requests/${id}`, { method: 'DELETE' }),
  },

  notifications: {
    list: () => 
      fetchApi<Notification[]>('/notifications'),
  },

  notificationChannels: {
    list: () => fetchApi<NotificationChannelRecord[]>('/notification-channels'),
    createTelegramLinkToken: () =>
      fetchApi<TelegramLinkTokenResponse>(
        '/notification-channels/telegram/link-token',
        { method: 'POST' }
      ),
    disconnectTelegram: () =>
      fetchApi<{ ok: boolean; channel?: NotificationChannelRecord; error?: string }>(
        '/notification-channels/telegram/disconnect',
        { method: 'POST' }
      ),
    connectEmail: (email: string) =>
      fetchApi<{ ok: boolean; channel?: NotificationChannelRecord; error?: string }>(
        '/notification-channels/email/connect',
        { method: 'POST', body: JSON.stringify({ email }) }
      ),
    disconnectEmail: () =>
      fetchApi<{ ok: boolean; channel?: NotificationChannelRecord; error?: string }>(
        '/notification-channels/email/disconnect',
        { method: 'POST' }
      ),
  },

  billing: {
    getPlan: () => fetchApi<UserPlan>('/billing/plan'),
    createCheckout: (interval: 'monthly' | 'annual', email?: string) =>
      fetchApi<{ url: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ interval, ...(email ? { email } : {}) }),
      }),
    createPortalSession: () =>
      fetchApi<{ url: string }>('/billing/portal', { method: 'POST' }),
  },

  parks: {
    featured: () => fetchApi<FeaturedPark[]>('/featured-parks'),
    directory: () => fetchApi<DirectoryPark[]>('/parks-directory'),
  },

  discovery: {
    getOrigins: () => fetchApi<OriginRecord[]>('/origins'),
    getPlaces: () => fetchApi<PlaceRecord[]>('/places'),
    getFacilities: (params?: { placeId: string }) => {
      const query = params?.placeId ? `?placeId=${encodeURIComponent(params.placeId)}` : '';
      return fetchApi<FacilityRecord[]>(`/facilities${query}`);
    },
    getSites: async (): Promise<SiteRecord[]> => {
      const PAGE_SIZE = 10000;
      const all: SiteRecord[] = [];
      let offset = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const page = await fetchApi<SiteRecord[]>(`/sites?limit=${PAGE_SIZE}&offset=${offset}`);
        all.push(...page);
        if (page.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }
      return all;
    },
    getPlaceDetails: (id: string) => fetchApi<PlaceDetails>(`/places/${id}/details`),
    getPlacesSummary: () => fetchApi<PlaceSummary[]>('/places-summary'),
    getAvailablePlaces: (startDate: string, endDate: string, minNights?: number) => {
      const params = new URLSearchParams({ startDate, endDate });
      if (minNights !== undefined && minNights > 1) {
        params.set('minNights', String(minNights));
      }
      return fetchApi<string[]>(`/available-places?${params.toString()}`);
    },
    getAvailableFacilitiesForPlace: (placeId: string, startDate: string, endDate: string, minNights?: number) => {
      const params = new URLSearchParams({ startDate, endDate });
      if (minNights !== undefined && minNights > 1) {
        params.set('minNights', String(minNights));
      }
      return fetchApi<string[]>(`/places/${encodeURIComponent(placeId)}/available-facilities?${params.toString()}`);
    },
    getAvailabilityRange: (params: {
      startDate: string;
      endDate: string;
      limit?: number;
    }) => {
      const query = new URLSearchParams();
      query.set('startDate', params.startDate);
      query.set('endDate', params.endDate);
      if (params.limit !== undefined) {
        query.set('limit', String(params.limit));
      }
      return fetchApi<AvailabilityRecord[]>(`/availabilities?${query.toString()}`);
    },
  },
};
